import { ReactNode, useState } from 'react';
import { AvailabilityBlock } from '@mews-commander/data-access';
import { LocalizedText, lt, showSuccessAlert, usePromiseState } from '@mews/framework';
import { Button, IconName, Stack } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { LocalizedLabel } from '@mews-commander/ui';
import { compactArray } from '@mews/fp';
import { createUpdateValue, useCommanderThunkDispatch } from '@mews-commander/utils';
import { updateAvailabilityBlocks } from '@mews-commander/legacy-entities/src/actions/availabilityBlocks';
import { fetchAccountsByIds } from '@mews-commander/legacy-entities/src/actions';
import { CompanyLink, CustomerLink } from '../../../../../components';
import { ClientInfoPanel } from './ClientInfoPanel';
import { ClientInfoFormValues } from './ClientInfoFields';

interface EntryWrapperProps {
    textKey: Mews.TextKey;
    value: ReactNode;
}
const EntryWrapper = ({ textKey, value }: EntryWrapperProps) => (
    <Stack spacing="100" itemAlignment="center">
        <LocalizedLabel variant="secondary" textKey={textKey} as="div" />
        {value}
    </Stack>
);

interface Props {
    availabilityBlock: AvailabilityBlock;
}
export const ClientInfoSection = ({ availabilityBlock }: Props) => {
    const {
        id: availabilityBlockId,
        bookerId,
        companyId,
        travelAgencyId,
        reservationPurpose,
        externalIdentifier,
        origin,
        serviceId,
        number,
    } = availabilityBlock;

    const dispatch = useCommanderThunkDispatch();
    const [isLoading, handleSubmit] = usePromiseState();
    const [isOpened, setOpened] = useState(false);

    const updateClientInfo = async (values: ClientInfoFormValues) => {
        await dispatch(
            updateAvailabilityBlocks({
                availabilityBlocks: [
                    {
                        availabilityBlockId,
                        bookerId: createUpdateValue(bookerId, values.bookerId || null),
                        companyId: createUpdateValue(companyId, values.companyId || null),
                        travelAgencyId: createUpdateValue(travelAgencyId, values.travelAgencyId || null),
                        reservationPurpose: createUpdateValue(reservationPurpose, values.reservationPurpose || null),
                    },
                ],
            })
        );
        const accountIds = compactArray([values.bookerId, values.companyId, values.travelAgencyId]);
        if (accountIds) await dispatch(fetchAccountsByIds({ accountIds }));

        showSuccessAlert({ description: lt('SaveSuccessMessage') });
        setOpened(false);
    };

    return (
        <>
            <Stack distribution="space-between">
                <Stack spacing="400" alignment="start" wrap>
                    {bookerId ? <EntryWrapper textKey={TextKey.Booker} value={<CustomerLink id={bookerId} />} /> : null}
                    {companyId ? (
                        <EntryWrapper textKey={TextKey.Company} value={<CompanyLink id={companyId} />} />
                    ) : null}
                    {travelAgencyId ? (
                        <EntryWrapper textKey={TextKey.TravelAgency} value={<CompanyLink id={travelAgencyId} />} />
                    ) : null}

                    <Button
                        onClick={() => setOpened(true)}
                        variant="ghost"
                        icon={IconName.ArrowRight}
                        iconPosition="right"
                        type="submit"
                    >
                        <LocalizedText textKey={TextKey.ViewMore} />
                    </Button>
                </Stack>

                {number ? (
                    <EntryWrapper textKey={TextKey.AvailabilityBlockNumber} value={<span>{number}</span>} />
                ) : null}
            </Stack>
            <ClientInfoPanel
                opened={isOpened}
                loading={isLoading}
                serviceId={serviceId}
                onClose={() => setOpened(false)}
                defaultValues={{
                    bookerId,
                    companyId,
                    travelAgencyId,
                    reservationPurpose,
                    origin,
                    externalIdentifier,
                }}
                onSubmit={handleSubmit(updateClientInfo)}
            />
        </>
    );
};
